import { Select as SelectPrimitive } from "@kobalte/core";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@habits/shared";

/**
 * Premium Select built on `@kobalte/core` — same pattern, tokens and animation
 * language as {@link ../ui/popover.tsx} and {@link ../ui/dialog.tsx}. It is
 * fully theme-safe (accent / accent-foreground pairs), keyboard + typeahead
 * accessible and exposes proper ARIA via Kobalte. Use it instead of native
 * `<select>` or ad-hoc dropdown buttons.
 *
 * @example
 * ```tsx
 * <Select
 *   value={value()}
 *   onChange={setValue}
 *   options={[{ value: "daily", label: t(msg`Daily`) }]}
 *   placeholder={t(msg`Pick one`)}
 * />
 * ```
 */
export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true.*/
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph.*/
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No habits yet"). */
  description?: string;
}

type ItemComponentProps<T extends string> = Parameters<
  NonNullable<SelectPrimitive.SelectRootProps<SelectOption<T>>["itemComponent"]>
>[0];

function SelectItem<T extends string>(props: ItemComponentProps<T>) {
  return (
    <SelectPrimitive.Item
      item={props.item}
      class={cn(
        "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm outline-none transition-colors",
        "text-popover-foreground",
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      )}
    >
      <SelectPrimitive.ItemLabel>{props.item.rawValue.label}</SelectPrimitive.ItemLabel>
      <SelectPrimitive.ItemIndicator>
        <Check class="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

interface SelectProps<T extends string = string> {
  options: SelectOption<T>[];
  /** Controlled value (the option's `value`). */
  value?: T | null;
  onChange?: (value: T | null) => void;
  placeholder?: JSX.Element;
  disabled?: boolean;
  /** Applied to the trigger button. */
  class?: string;
  /** Applied to the dropdown content panel. */
  contentClass?: string;
  "aria-label"?: string;
  name?: string;
  id?: string;
  "data-testid"?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA.*/
  emptyLabel?: string;
}

export function Select<T extends string = string>(props: SelectProps<T>) {
  const [local, others] = splitProps(props, [
    "options",
    "value",
    "onChange",
    "placeholder",
    "disabled",
    "class",
    "contentClass",
    "aria-label",
    "data-testid",
    "emptyAction",
    "emptyLabel",
  ]);

  const selectedOption = () => local.options.find((o) => o.value === local.value) ?? null;
  const isEmpty = () => local.options.length === 0;

  return (
    <SelectPrimitive.Root<SelectOption<T>>
      {...others}
      options={local.options}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      disabled={local.disabled}
      value={selectedOption()}
      // CC (Select re-click): re-clicking the already selected option must NOT
      // unselect it. Kobalte's default toggle emits null on a
      // re-click → coercion to null → empty field. It is a required single-select:
      // the value can be changed to another option but not cleared by re-clicking.
      disallowEmptySelection
      onChange={(option) => local.onChange?.(option?.value ?? null)}
      placeholder={local.placeholder}
      itemComponent={SelectItem}
    >
      <SelectPrimitive.Trigger
        data-testid={local["data-testid"]}
        aria-label={local["aria-label"]}
        class={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
          "transition-colors hover:border-accent/60",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          local.class,
        )}
      >
        <SelectPrimitive.Value<SelectOption<T>> class="truncate">
          {(state) => (
            <Show
              when={state.selectedOption()}
              fallback={<span class="text-muted-foreground">{local.placeholder}</span>}
            >
              {(option) => option().label}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 opacity-60">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[220] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-60 overflow-y-auto p-1" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
